import { useFacilityMode } from '@/entities/facility';
import { useMemo } from 'react';

type UseFacilityScopeOptions = {
  /**
   * A facility this table is already hard-scoped to by its container (e.g. the
   * residents table embedded in a single-facility tab). Takes precedence over
   * the global mode.
   */
  facilityId?: string | number;
};

/**
 * Single source of truth for "is this table showing one facility or all of
 * them", per card #157 Part 4.
 *
 * Single Mode hides the Facility column and the Facility filter — every row is
 * the same facility, so both are dead weight — and forces the query to that
 * facility regardless of any filter the user left behind in Portfolio Mode.
 */
export const useFacilityScope = ({
  facilityId,
}: UseFacilityScopeOptions = {}) => {
  const { isSingleMode, selectedFacilityId } = useFacilityMode();

  return useMemo(() => {
    const scopedId = facilityId ?? (isSingleMode ? selectedFacilityId : null);
    const isScoped = scopedId != null;

    return {
      isScoped,
      scopedFacilityId: scopedId != null ? Number(scopedId) : null,
      showFacilityColumn: !isScoped,
      showFacilityFilter: !isScoped,
      /**
       * Facility ids to send with the query. In Single Mode this overrides the
       * user's own filter selection; in Portfolio Mode it passes theirs through.
       */
      resolveFacilitiesIds: (userSelection: number[] | undefined) =>
        isScoped ? [Number(scopedId)] : userSelection,
    };
  }, [facilityId, isSingleMode, selectedFacilityId]);
};
